import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import {
  MemCommonMarkdown,
  MemRoutableItemKind,
  type MemCommonMarkdownProps,
} from "@mem-labs/common-editor";
import type { FC, MouseEvent } from "react";
import { observer } from "mobx-react-lite";
import { FloatingNotePreview, HEADS_UP_PREVIEW_PLACEMENT } from "@/components/item-preview";
import { SmartCardSection } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { HeadsUpLabsBadge } from "@/components/heads-up/cards/common/heads-up-labs-badge";
import { useHeadsUpPreviewHover } from "@/components/heads-up/cards/common/use-heads-up-preview-hover";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";
import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { MeetingBriefingFeedback } from "@/components/heads-up/cards/meeting-briefing-feedback";
import type { MeetingBriefingSmartCard } from "@/components/heads-up/heads-up.types";
import type {
  HeadsUpCardRendererAnalytics,
  HeadsUpCardItemMeta,
} from "@/components/heads-up/cards/registry";
import type { Maybe } from "@/domains/common/types";
import { HeadsUpItemSource } from "@/components/heads-up/analytics";
import { MDSChip, MDSChipVariant } from "@/mds";
import { MDSIconKind } from "@/mds/components/icon";
import { useAppStore } from "@/store";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

type Props = {
  card: MeetingBriefingSmartCard;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
  analytics?: HeadsUpCardRendererAnalytics;
};

type MeetingBriefingNoteChipItem = {
  id: string;
  primary: string;
  secondary: string | null;
  positionIndex: number;
};

type MeetingBriefingNoteChipProps = {
  item: MeetingBriefingNoteChipItem;
  onClick: (event: unknown) => void;
  notePreviewInfo?: Maybe<SmartNotePreviewInfo>;
  onPreviewOpen?: () => void;
};

function extractNoteIdsFromValue(value: unknown, noteIds: Set<string>): void {
  if (Array.isArray(value)) {
    value.forEach(item => extractNoteIdsFromValue(item, noteIds));
    return;
  }

  if (!value || typeof value !== "object") return;

  Object.entries(value).forEach(([key, entryValue]) => {
    if (key === "note_id" && typeof entryValue === "string" && entryValue.trim()) {
      noteIds.add(entryValue);
      return;
    }

    if (key === "note_ids" && Array.isArray(entryValue)) {
      entryValue.forEach(noteId => {
        if (typeof noteId === "string" && noteId.trim()) {
          noteIds.add(noteId);
        }
      });
      return;
    }

    extractNoteIdsFromValue(entryValue, noteIds);
  });
}

function extractNoteIdsFromToolArgs(args: unknown): string[] {
  const noteIds = new Set<string>();
  extractNoteIdsFromValue(args, noteIds);
  return [...noteIds];
}

function buildNoteLinksMarkdown(noteIds: string[]): string {
  return noteIds.map(noteId => `[${noteId}](mem://notes/${encodeURIComponent(noteId)})`).join(", ");
}

function buildTraceMarkdown(card: MeetingBriefingSmartCard): string {
  const lines: string[] = [];
  lines.push("---");
  lines.push("#### Agent trace");
  lines.push("");

  const toolCalls = card.value.tool_calls ?? [];
  if (toolCalls.length === 0) {
    lines.push("- No tool calls recorded.");
  } else {
    toolCalls.forEach((call, index) => {
      const tool = typeof call.tool === "string" ? call.tool : "unknown";
      const args = call.args ? JSON.stringify(call.args) : "{}";
      lines.push(`${index + 1}. \`${tool}(${args})\``);
      const noteIds = extractNoteIdsFromToolArgs(call.args);
      if (noteIds.length > 0) {
        lines.push(`   - Related notes: ${buildNoteLinksMarkdown(noteIds)}`);
      }
      if (typeof call.result_count === "number") {
        lines.push(`   - Found ${call.result_count} results`);
      }
      if (typeof call.content_length === "number") {
        lines.push(`   - Read ${call.content_length} characters`);
      }
      if (typeof call.error === "string" && call.error) {
        lines.push(`   - Error: ${call.error}`);
      }
    });
  }

  const reasoning = card.value.reasoning;
  if (reasoning) {
    lines.push("");
    lines.push("#### Agent reasoning");
    lines.push("");
    lines.push(reasoning.trim() || "_No reasoning recorded._");
  }

  const timing = card.value.timing;
  if (timing) {
    lines.push("");
    lines.push("#### Timing");
    lines.push("");
    if (typeof timing.tool_calls === "number") {
      lines.push(`- Tool calls: ${timing.tool_calls}`);
    }
    if (typeof timing.total_time_seconds === "number") {
      lines.push(`- Total time: ${timing.total_time_seconds.toFixed(2)}s`);
    }
  }

  return lines.join("\n");
}

const MeetingBriefingNoteChip = observer(function MeetingBriefingNoteChip({
  item,
  onClick,
  notePreviewInfo,
  onPreviewOpen,
}: MeetingBriefingNoteChipProps) {
  const { hoverState, handleMouseEnter, handleMouseLeave } = useHeadsUpPreviewHover({
    noteId: item.id,
    notePreviewInfo,
    onPreviewOpen,
  });

  return (
    <NoteChipWrapper>
      <MDSChip
        icon={MDSIconKind.Document}
        variant={MDSChipVariant.Small}
        aria-label={item.primary}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {item.primary}
      </MDSChip>
      {hoverState.hovering && notePreviewInfo?.noteObservable ? (
        <FloatingNotePreview
          state={hoverState}
          noteObservable={notePreviewInfo.noteObservable}
          placement={HEADS_UP_PREVIEW_PLACEMENT}
        />
      ) : null}
    </NoteChipWrapper>
  );
});

export const MeetingBriefingSmartCardRenderer: FC<Props> = ({
  card,
  getNotePreviewInfo,
  analytics,
}) => {
  const clickHandlers = useHeadsUpClick();
  const { store } = useAppStore();
  const isDebugMode = store.debug.debugModeEnabled;
  const [showTrace, setShowTrace] = useState(false);

  const title = card.value.title ?? "Meeting Briefing";
  const markdown = card.value.markdown;
  const noteItems = useMemo(() => card.value.note_items ?? [], [card.value.note_items]);

  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) return;
    if (!markdown && noteItems.length === 0) return;
    hasTrackedView.current = true;
    trackEvent(TrackedEvent.MeetingBriefingView, {
      connected_calendar_event_id: card.value.connected_calendar_event_id ?? null,
      note_ids: noteItems.map(item => item.note_id),
      markdown: markdown ?? null,
    });
  }, [card.id, card.value.connected_calendar_event_id, noteItems, markdown]);

  useEffect(() => {
    if (!isDebugMode) {
      setShowTrace(false);
    }
  }, [isDebugMode]);

  const hasTrace =
    isDebugMode &&
    Boolean(
      (card.value.reasoning && card.value.reasoning.trim()) ||
      (card.value.tool_calls && card.value.tool_calls.length > 0)
    );

  const traceMarkdown = useMemo(() => (hasTrace ? buildTraceMarkdown(card) : ""), [card, hasTrace]);

  const baseItemSource = card.originated_from_find_more
    ? HeadsUpItemSource.FindMore
    : HeadsUpItemSource.Standard;

  const items = useMemo(
    () =>
      noteItems.map((item, index) => ({
        id: item.note_id,
        primary: item.primary_label ?? "Untitled",
        secondary: item.secondary_label,
        positionIndex: index,
      })),
    [noteItems]
  );

  const buildAnalyticsMeta = useCallback(
    (item: (typeof items)[number]): HeadsUpCardItemMeta => ({
      itemKind: "note",
      itemId: item.id,
      itemTitle: item.primary,
      itemPositionIndex: item.positionIndex,
      itemSource: baseItemSource,
    }),
    [baseItemSource]
  );

  const handleNoteChipClick = useCallback(
    (item: (typeof items)[number]) => (event: unknown) => {
      trackEvent(TrackedEvent.MeetingBriefingNoteChipInteract, {
        connected_calendar_event_id: card.value.connected_calendar_event_id ?? null,
        action: "click",
        note_id: item.id,
        note_title: item.primary,
      });
      analytics?.onItemClick?.(buildAnalyticsMeta(item));
      const handler = clickHandlers?.noteClick(item.id);
      handler?.(event as unknown as MouseEvent<HTMLElement>);
    },
    [analytics, buildAnalyticsMeta, clickHandlers, card.value.connected_calendar_event_id]
  );

  const handleClickMemItem = useCallback<MemCommonMarkdownProps["onClickMemItem"]>(
    (itemKind, itemId, _target, event) => {
      if (!event) return;

      if (itemKind === MemRoutableItemKind.NOTES) {
        const handler = clickHandlers?.noteClick(itemId);
        handler?.(event as unknown as MouseEvent<HTMLElement>);
        return;
      }

      if (itemKind === MemRoutableItemKind.COLLECTIONS) {
        const handler = clickHandlers?.collectionClick(itemId);
        handler?.(event as unknown as MouseEvent<HTMLElement>);
      }
    },
    [clickHandlers]
  );

  const connectedCalendarEventId = card.value.connected_calendar_event_id;

  const headerExtra = connectedCalendarEventId ? (
    <MeetingBriefingFeedback
      connectedCalendarEventId={connectedCalendarEventId}
      markdownContent={markdown ?? null}
      noteIds={noteItems.map(item => item.note_id)}
    />
  ) : (
    <LabsBadgeWrapper>
      <HeadsUpLabsBadge />
    </LabsBadgeWrapper>
  );

  if (!markdown && noteItems.length === 0) return null;

  return (
    <SmartCardSection>
      <CollapsibleSectionHeader title={title} headerExtra={headerExtra}>
        {markdown ? (
          <MeetingBriefingMarkdownContent onClickMemItem={handleClickMemItem}>
            {markdown}
          </MeetingBriefingMarkdownContent>
        ) : null}

        {items.length > 0 ? (
          <NoteChipList>
            {items.map(item => (
              <MeetingBriefingNoteChip
                key={item.id}
                item={item}
                onClick={handleNoteChipClick(item)}
                notePreviewInfo={getNotePreviewInfo?.(item.id)}
                onPreviewOpen={() => {
                  trackEvent(TrackedEvent.MeetingBriefingNoteChipInteract, {
                    connected_calendar_event_id: card.value.connected_calendar_event_id ?? null,
                    action: "hover",
                    note_id: item.id,
                    note_title: item.primary,
                  });
                  analytics?.onItemPreviewOpen?.(buildAnalyticsMeta(item));
                }}
              />
            ))}
          </NoteChipList>
        ) : null}

        {hasTrace ? (
          <TraceToggle onClick={() => setShowTrace(prev => !prev)}>
            {showTrace ? "Hide agent trace" : "Show agent trace"}
          </TraceToggle>
        ) : null}

        {showTrace && traceMarkdown ? (
          <MarkdownContent onClickMemItem={handleClickMemItem}>{traceMarkdown}</MarkdownContent>
        ) : null}
      </CollapsibleSectionHeader>
    </SmartCardSection>
  );
};

const LabsBadgeWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
`;

const NoteChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing["1"]};
  margin-top: ${({ theme }) => theme.spacing["2"]};
  margin-left: ${({ theme }) => theme.spacing["7"]};
`;

const NoteChipWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const MarkdownContent = styled(MemCommonMarkdown)`
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: ${({ theme }) => theme.spacing["2"]};
  margin-left: ${({ theme }) => theme.spacing["7"]};
  user-select: text;

  * {
    user-select: text;
  }
`;

const MeetingBriefingMarkdownContent = styled(MarkdownContent)`
  margin-top: 0;
  margin-left: ${({ theme }) => theme.spacing["7"]};
`;

const TraceToggle = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing["1"]} 0;
  margin-left: ${({ theme }) => theme.spacing["7"]};
  margin-top: ${({ theme }) => theme.spacing["1"]};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;
